import { keepPreviousData, type QueryKey, useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import type { Page } from '@/types/api.types';

export interface InfiniteListOptions<T> {
  /** Must differ from the key of any ordinary (single-page) query on the same list. */
  queryKey: QueryKey;
  /** One page of the list — the feature's own `api/*.api.ts` function. */
  fetchPage: (page: number, signal: AbortSignal) => Promise<Page<T>>;
  enabled?: boolean;
}

/**
 * A paginated API list loaded a page at a time as the user scrolls (with `ScrollList`). Returns
 * every row loaded so far, the total on the server, and `loadMore` for the next page.
 */
export function useInfiniteList<T>({
  queryKey,
  fetchPage,
  enabled = true,
}: InfiniteListOptions<T>) {
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam, signal }) => fetchPage(pageParam, signal),
    initialPageParam: 1,
    // A new search or filter keeps the old rows on screen until its first page arrives.
    placeholderData: keepPreviousData,
    getNextPageParam: (last) =>
      last.meta.page < last.meta.totalPages ? last.meta.page + 1 : undefined,
    enabled,
  });

  const items = useMemo(() => query.data?.pages.flatMap((page) => page.items), [query.data]);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    /** Every row loaded so far; `undefined` until the first page arrives. */
    items,
    /** How many rows the server has in all. */
    total: query.data?.pages[0]?.meta.total,
    hasMore: hasNextPage,
    isLoadingMore: isFetchingNextPage,
    loadMore,
    isPending: query.isPending,
    /** Fetching anything — a new search, a refresh or the next page. */
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
